import axios from 'axios'

// 统一响应结构
export interface ApiResponse<T = any> {
  code: number
  data: T
  message?: string
}

const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
}, (error) => Promise.reject(error))

api.interceptors.response.use(
  (response) => response.data as any,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      // 如果已经在登录页，不跳转（让Login.tsx的catch显示错误消息�?      
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// 统一请求函数
const rp = <T = any>(url: string, data?: any): Promise<ApiResponse<T>> =>
  api.post(url, data) as any
const rg = <T = any>(url: string, params?: any): Promise<ApiResponse<T>> =>
  api.get(url, { params }) as any
const rput = <T = any>(url: string, data?: any): Promise<ApiResponse<T>> =>
  api.put(url, data) as any
const rdel = <T = any>(url: string): Promise<ApiResponse<T>> =>
  api.delete(url) as any

// ========== API 封装 ==========

export const authApi = {
  login: (data: { phone: string; password: string }) =>
    rp<{ token: string; user: any }>('/auth/login', data),
  register: (data: any) => rp('/auth/register', data),
  sendCode: (data: { phone: string; type: string }) =>
    rp('/auth/send-code', data),
  refresh: (refreshToken: string) =>
    rp('/auth/refresh', { refreshToken })
}

// 管理端员工认�?
export const staffAuthApi = {
  login: (data: { username: string; password: string }) =>
    rp<{ token: string; user: any }>('/staff-auth/login', data),
  me: () => rg('/staff-auth/me')
}

export const categoryApi = {
  getCategories: () => rg('/products/categories'),
  createCategory: (data: any) => rp('/admin/categories', data),
  updateCategory: (id: number, data: any) => rput(`/admin/categories/${id}`, data),
  deleteCategory: (id: number) => rdel(`/admin/categories/${id}`),
  createSubCategory: (data: any) => rp('/admin/sub-categories', data),
  updateSubCategory: (id: number, data: any) => rput(`/admin/sub-categories/${id}`, data),
  deleteSubCategory: (id: number) => rdel(`/admin/sub-categories/${id}`)
}

export const productApi = {
  getCategories: () => rg('/products/categories'),
  getProducts: (params?: any) => rg('/products', params),
  getProduct: (id: number) => rg(`/products/${id}`),
  createProduct: (data: any) => rp('/admin/products', data),
  updateProduct: (id: number, data: any) => rput(`/admin/products/${id}`, data),
  deleteProduct: (id: number) => rdel(`/admin/products/${id}`),
  updateStatus: (id: number, status: number) =>
    rput(`/admin/products/${id}/status`, { status })
}

export const orderApi = {
  getOrders: (params?: any) => rg('/orders', params),
  getOrder: (id: number) => rg(`/orders/${id}`),
  createOrder: (data: any) => rp('/orders', data),
  updateStatus: (id: number, data: any) => rput(`/orders/${id}/status`, data),
  cancel: (id: number, data?: any) => rp(`/orders/${id}/cancel`, data),
  payBalance: (id: number) => rp(`/orders/${id}/pay-balance`),
  deleteOrder: (id: number) => rdel(`/orders/${id}`)
}

export const userApi = {
  getProfile: () => rg('/users/profile'),
  updateProfile: (data: any) => rput('/users/profile', data),
  getAddresses: () => rg('/users/addresses'),
  addAddress: (data: any) => rp('/users/addresses', data),
  getUsers: (params?: any) => rg('/admin/users', params),
  getUser: (id: number) => rg(`/admin/users/${id}`),
  updateUserStatus: (id: number, status: number) =>
    rput(`/admin/users/${id}/status`, { status })
}

export const statsApi = {
  getDashboard: (params?: any) => rg('/admin/stats/dashboard', params),
  getTopProducts: (params?: any) => rg('/admin/stats/top-products', params),
  getTopCustomers: (params?: any) => rg('/admin/stats/top-customers', params),
  getDailySales: (params?: any) => rg('/admin/stats/daily-sales', params)
}

export const employeeApi = {
  getEmployees: (params?: any) => rg('/employees', params),
  getEmployee: (id: number) => rg(`/employees/${id}`),
  createEmployee: (data: any) => rp('/employees', data),
  updateEmployee: (id: number, data: any) => rput(`/employees/${id}`, data),
  resetPassword: (id: number, password: string) =>
    rput(`/employees/${id}/password`, { password }),
  deleteEmployee: (id: number) => rdel(`/employees/${id}`),
  getRoles: () => rg('/employees/roles'),
  createRole: (data: any) => rp('/employees/roles', data),
  updateRole: (id: number, data: any) => rput(`/employees/roles/${id}`, data),
  deleteRole: (id: number) => rdel(`/employees/roles/${id}`)
}

export const departmentApi = {
  getList: (params?: any) => rg('/departments', params),
  getTree: () => rg('/departments', { tree: '1' }),
  getOptions: () => rg('/departments/options'),
  create: (data: any) => rp('/departments', data),
  update: (id: number, data: any) => rput(`/departments/${id}`, data),
  delete: (id: number) => rdel(`/departments/${id}`)
}

export const positionApi = {
  getList: (params?: any) => rg('/positions', params),
  getOptions: (params?: any) => rg('/positions/options', params),
  create: (data: any) => rp('/positions', data),
  update: (id: number, data: any) => rput(`/positions/${id}`, data),
  delete: (id: number) => rdel(`/positions/${id}`)
}

export const customerApi = {
  getCustomers: (params?: any) => rg('/customers', params),
  getCustomer: (id: number) => rg(`/customers/${id}`),
  getCustomerDetail: (id: number) => rg(`/customers/${id}/detail`),
  createCustomer: (data: any) => rp('/customers', data),
  updateCustomer: (id: number, data: any) => rput(`/customers/${id}`, data),
  deleteCustomer: (id: number) => rdel(`/customers/${id}`)
}

export const enterpriseClientsApi = {
  getList: (params?: any) => rg('/enterprise-clients', params),
  get: (id: number) => rg(`/enterprise-clients/${id}`),
  create: (data: any) => rp('/enterprise-clients', data),
  update: (id: number, data: any) => rput(`/enterprise-clients/${id}`, data),
  delete: (id: number) => rdel(`/enterprise-clients/${id}`)
}

export const inventoryApi = {
  getWarehouses: () => rg('/inventory/warehouses'),
  getSummary: (params?: any) => rg('/inventory/summary', params),  // 添加汇总接�?
  createWarehouse: (data: any) => rp('/inventory/warehouses', data),
  updateWarehouse: (id: number, data: any) =>
    rput(`/inventory/warehouses/${id}`, data),
  deleteWarehouse: (id: number) => rdel(`/inventory/warehouses/${id}`),
  getInventory: (params?: any) => rg('/inventory/list', params),
  stockIn: (data: any) => rp('/inventory/stock-in', data),
  stockOut: (data: any) => rp('/inventory/out', data),
  reportLoss: (data: any) => rp('/inventory/report-loss', data),
  auditLoss: (data: any) => rp('/inventory/report-loss/audit', data),
  getPendingLoss: (params?: any) => rg('/inventory/report-loss/pending', params),
  getBatches: (params?: any) => rg('/inventory/batches', params),
  getLogs: (params?: any) => rg('/inventory/logs', params)
}

export const warehouseApi = {
  getList: () => rg('/inventory/warehouses'),
  create: (data: any) => rp('/inventory/warehouses', data),
  update: (id: number, data: any) => rput(`/inventory/warehouses/${id}`, data),
  delete: (id: number) => rdel(`/inventory/warehouses/${id}`)
}

export const bomApi = {
  getBOMs: (params?: any) => rg('/bom', params),
  getBOM: (id: number) => rg(`/bom/${id}`),
  createBOM: (data: any) => rp('/bom', data),
  updateBOM: (id: number, data: any) => rput(`/bom/${id}`, data),
  deleteBOM: (id: number) => rdel(`/bom/${id}`),
  updateStatus: (id: number, status: number) =>
    rput(`/bom/${id}/status`, { status })
}

export const productionApi = {
  getPlans: (params?: any) => rg('/production', params),
  getPlan: (id: number) => rg(`/production/${id}`),
  createPlan: (data: any) => rp('/production', data),
  completePlan: (id: number, data: any) => rp(`/production/${id}/complete`, data),
  cancelPlan: (id: number) => rp(`/production/${id}/cancel`),
  deletePlan: (id: number) => rdel(`/production/${id}`)
}

export const supplierApi = {
  getList: (params?: any) => rg('/suppliers', params),
  get: (id: number) => rg(`/suppliers/${id}`),
  create: (data: any) => rp('/suppliers', data),
  update: (id: number, data: any) => rput(`/suppliers/${id}`, data),
  delete: (id: number) => rdel(`/suppliers/${id}`)
}

export const purchaseApi = {
  getList: (params?: any) => rg('/purchase', params),
  get: (id: number) => rg(`/purchase/${id}`),
  create: (data: any) => rp('/purchase', data),
  update: (id: number, data: any) => rput(`/purchase/${id}`, data),
  delete: (id: number) => rdel(`/purchase/${id}`),
  audit: (id: number) => rp(`/purchase/${id}/audit`),
  receive: (id: number, data: any) => rp(`/purchase/${id}/receive`, data),
  cancel: (id: number) => rp(`/purchase/${id}/cancel`),
  pay: (id: number, data: any) => rp(`/purchase/${id}/pay`, data),
  getPendingReceipts: () => rg('/purchase/pending/receipts')
}

export const deliveryApi = {
  getList: (params?: any) => rg('/delivery-tasks', params),
  get: (id: number) => rg(`/delivery-tasks/${id}`),
  create: (data: any) => rp('/delivery-tasks', data),
  assign: (id: number, data: any) => rp(`/delivery-tasks/${id}/assign`, data),
  startDelivery: (id: number) => rp(`/delivery-tasks/${id}/start`),
  complete: (id: number) => rp(`/delivery-tasks/${id}/complete`),
  cancel: (id: number) => rp(`/delivery-tasks/${id}/cancel`),
  delete: (id: number) => rdel(`/delivery-tasks/${id}`)
}

export const deliveryDriverApi = {
  getList: (params?: any) => rg('/delivery-drivers', params),
  getAvailableEmployees: (params?: any) => rg('/delivery-drivers/available-employees', params),
  create: (data: any) => rp('/delivery-drivers', data),
  update: (id: number, data: any) => rput(`/delivery-drivers/${id}`, data),
  delete: (id: number) => rdel(`/delivery-drivers/${id}`),
  autoAssign: (data: { district_name: string; city_name?: string }) => rp('/delivery-drivers/auto-assign', data)
}

export const pickupPointApi = {
  getList: (params?: any) => rg('/pickup-locations', params),
  getAdminList: (params?: any) => rg('/pickup-locations/admin/list', params),
  get: (id: number) => rg(`/pickup-locations/admin/${id}`),
  create: (data: any) => rp('/pickup-locations/admin', data),
  update: (id: number, data: any) => rput(`/pickup-locations/admin/${id}`, data),
  delete: (id: number) => rdel(`/pickup-locations/admin/${id}`),
  toggleStatus: (id: number) => rput(`/pickup-locations/admin/${id}/status`)
}

export const adsApi = {
  getList: (params?: any) => rg('/ads', params),
  get: (id: number) => rg(`/ads/${id}`),
  create: (data: any) => rp('/ads', data),
  update: (id: number, data: any) => rput(`/ads/${id}`, data),
  delete: (id: number) => rdel(`/ads/${id}`)
}

export const receivablesApi = {
  getList: (params?: any) => rg('/receivables', params),
  get: (id: number) => rg(`/receivables/${id}`),
  create: (data: any) => rp('/receivables', data),
  pay: (id: number, data: any) => rp(`/receivables/${id}/pay`, data),
  delete: (id: number) => rdel(`/receivables/${id}`)
}

// ========== 辅料管理 ==========
export const auxiliaryApi = {
  // 商品管理
  getList: (params?: any) => rg('/products', { ...params, product_type: 2 }),
  create: (data: any) => rp('/products/auxiliary', data),
  update: (id: number, data: any) => rput(`/admin/products/${id}`, data),
  delete: (id: number) => rdel(`/admin/products/${id}`),
  getCategories: () => rg('/products/categories'),
  // 库存管理
  getInventoryList: (params?: any) => rg('/auxiliary/list', params),
  getProducts: () => rg('/auxiliary/products'),
  getWarehouses: () => rg('/auxiliary/warehouses'),
  stockIn: (data: any) => rp('/auxiliary/stock-in', data),
  stockOut: (data: any) => rp('/auxiliary/stock-out', data),
  adjust: (data: any) => rp('/auxiliary/adjust', data),
  getLogs: (params?: any) => rg('/auxiliary/logs', params)
}

export const memberApi = {
  getLevels: () => rg('/member/levels'),
  createLevel: (data: any) => rp('/member/levels', data),
  updateLevel: (id: number, data: any) => rput(`/member/levels/${id}`, data),
  deleteLevel: (id: number) => rdel(`/member/levels/${id}`)
}

export const monthlyBillingApi = {
  getCustomers: (params?: any) => rg('/monthly-billing/admin/customers', params),
  audit: (id: number, data: any) => rput(`/monthly-billing/admin/audit/${id}`, data),
  getBillings: (params?: any) => rg('/monthly-billing/admin/billings', params),
  generate: (data: any) => rp('/monthly-billing/admin/generate', data)
}

export const invoiceApi = {
  getList: (params?: any) => rg('/invoices/admin/list', params),
  getDetail: (id: number) => rg('/invoices/admin/' + id),
  handle: (id: number, data: any) => rput('/invoices/' + id + '/handle', data),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/invoices/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  saveFile: (id: number, file_path: string) => api.put('/api/invoices/' + id + '/file', { file_path }),
  getFileUrl: (filename: string) => `/api/invoices/file/${filename}`
}

// 财务模块 API
export const financeApi = {
  // 会计科目
  getAccounts: (params?: any) => rg('/finance/accounts', params),
  getAccountsTree: () => rg('/finance/accounts/tree'),

  // 银行账户
  getBankAccounts: () => rg('/finance/bank-accounts'),
  createBankAccount: (data: any) => rp('/finance/bank-accounts', data),
  updateBankAccount: (id: number, data: any) => rput(`/finance/bank-accounts/${id}`, data),
  deleteBankAccount: (id: number) => rdel(`/finance/bank-accounts/${id}`),

  // 收支记录
  getExpenseRecords: (params?: any) => rg('/finance/expense-records', params),
  createExpenseRecord: (data: any) => rp('/finance/expense-records', data),
  auditExpenseRecord: (id: number, data: any) => rput(`/finance/expense-records/${id}/audit`, data),
  getExpenseStats: (params?: any) => rg('/finance/expense-records/stats', params),

  // 会计凭证
  getVouchers: (params?: any) => rg('/finance/vouchers', params),
  getVoucher: (id: number) => rg(`/finance/vouchers/${id}`),
  createVoucher: (data: any) => rp('/finance/vouchers', data),
  auditVoucher: (id: number, data?: any) => rput(`/finance/vouchers/${id}/audit`, data || {}),
  deleteVoucher: (id: number) => rdel(`/finance/vouchers/${id}`),

  // 财务报表
  getBalanceSheet: (params?: any) => rg('/finance/reports/balance-sheet', params),
  getIncomeStatement: (params?: any) => rg('/finance/reports/income-statement', params),
  getCashFlow: (params?: any) => rg('/finance/reports/cash-flow', params),
}

export const purchaseInvoiceApi = {
  getList: (params?: any) => rg('/purchase-invoices', params),
  getDetail: (id: number) => rg('/purchase-invoices/' + id),
  getSuppliers: () => rg('/purchase-invoices/suppliers'),
  getPurchaseOrders: (supplierId: number) => rg('/purchase-invoices/purchase-orders', { supplier_id: supplierId }),
  create: (data: any) => rp('/purchase-invoices', data),
  audit: (id: number, data: any) => rp('/purchase-invoices/' + id + '/audit', data),
  delete: (id: number) => rdel('/purchase-invoices/' + id),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/purchase-invoices/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getFileUrl: (filename: string) => `/api/purchase-invoices/file/${filename}`
}

export const permissionApi = {
  getPermissions: () => rg('/permissions/permissions'),
  initPermissions: () => rp('/permissions/permissions/init'),
  getRoles: () => rg('/permissions/roles'),
  createRole: (data: any) => rp('/permissions/roles', data),
  updateRole: (id: number, data: any) => rput(`/permissions/roles/${id}`, data),
  deleteRole: (id: number) => rdel(`/permissions/roles/${id}`),
  getStaffList: () => rg('/permissions/staff-list'),
  getStaffPermissions: (employeeId: number) => rg(`/permissions/staff-permissions/${employeeId}`),
  assignStaffPermissions: (data: { employee_id: number; user_id?: number; permission_ids: number[] }) => rp('/permissions/staff-permissions', data),
  getMyPermissions: () => rg('/permissions/my-permissions')
}

// ========== 对账单管�?==========
export const statementsApi = {
  getList: (params?: any) => rg('/statements', params),
  getDetail: (id: number) => rg(`/statements/${id}`),
  generate: (data: any) => rp('/statements/generate', data),
  confirm: (id: number) => rput(`/statements/${id}/confirm`, {}),
  getPdfUrl: (id: number) => `/api/statements/${id}/pdf`
}

// ========== 发票申请 ==========
export const invoiceApplicationsApi = {
  getList: (params?: any) => rg('/invoices', params),
  create: (data: any) => rp('/invoices', data),
  complete: (id: number, data: any) => rput(`/invoices/${id}/complete`, data)
}

// ========== 审批管理 ==========
export const approvalApi = {
  getPending: (employeeId: number) => rg('/approval/pending', { userId: employeeId }),
  getMyRequests: (employeeId: number) => rg('/approval/my-requests', { userId: employeeId }),
  getDetail: (id: number) => rg(`/approval/requests/${id}`),
  review: (id: number, data: any) => rp(`/approval/requests/${id}/review`, data),
  withdraw: (id: number) => rp(`/approval/requests/${id}/withdraw`, {}),
  resubmit: (id: number, data: any) => rp(`/approval/requests/${id}/resubmit`, data),
  cancel: (id: number) => rp(`/approval/requests/${id}/cancel`, {}),
  getStats: (employeeId: number) => rg('/approval/stats', { userId: employeeId })
}

export default api

